import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tournament } from '../types/types';
import SyncPlayerBtn from '../components/customLayout/SyncPlayerBtn';
import tournamentsEmptyState from '../assets/images/tournaments-item.svg';
import tournamentsSearchState from '../assets/images/tournaments-item-search.svg';
import { FileText } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import axiosInstance from '../axiosConfig';
import { useAuthStore } from '../store/authStore';
import { useClubStore } from '../store/clubStore';
import { useGlobalStore } from '../store/globalStore';
import { useTournamentStore } from '../store/tournamentStore';

// Parse date like "23.05.2025 18:00" (dd.MM.yyyy HH:mm) into a timestamp (ms)
const parseGermanDateTime = (value: string): number | null => {
  if (!value) return null;
  const [datePart, timePart] = value.trim().split(' ');
  if (!datePart) return null;
  const [dd, mm, yyyy] = datePart.split('.');
  if (!dd || !mm || !yyyy) return null;
  let hh = 0;
  let min = 0;
  if (timePart) {
    const [h, m] = timePart.split(':');
    hh = Number(h) || 0;
    min = Number(m) || 0;
  }
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), hh, min, 0, 0);
  const t = d.getTime();
  return isNaN(t) ? null : t;
};

const TournamentsPage: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const myClub = useClubStore((state) => state.myClub);
  const setLoading = useGlobalStore((state) => state.setLoading);
  const isLoading = useGlobalStore((state) => state.isLoading);
  const setActiveTab = useTournamentStore((state) => state.setActiveTab);
  const resetTournament = useTournamentStore((state) => state.resetTournament);

  const tableHeader = [
    { key: 'date', text: '📅 Datum' },
    { key: 'tournament', text: '🏆 Turnier' },
    { key: 'deadline', text: '⏰ Meldeschluss' },
    { key: 'action', text: '⚙️ Aktion' },
  ];

  const geTournaments = async () => {
    setLoading(true);
    const response = await axiosInstance.get('/tournaments');
    const res = response.data;

    setData(res);
    setLoading(false);
  };

  const generateDocFileUrl = (docFile: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return `${import.meta.env.VITE_APP_BASE_URL}${docFile}?time=${new Date().getTime()}`;
  };

  const resetTorunamentData = () => {
    setActiveTab('capture');
    resetTournament();
  };

  const [data, setData] = useState<Tournament[]>([]);

  useEffect(() => {
    geTournaments();
  }, [token]);

  return (
    <div className="bg-white border rounded-md p-4 mx-5">
      <div className="font-medium text-lg mb-4">Turniere</div>

      {isLoading ? (
        <div className="h-96 flex items-center justify-center">
          <div className="text-center">
            <img
              className="mx-auto py-10 animate-bounce"
              alt="tournaments not find "
              src={tournamentsSearchState}
            />
            <div className="text-2xl">Bitte warten Sie.</div>
            Wir durchsuchen die Datenbank, um Ihre Turnierliste zu finden...
          </div>
        </div>
      ) : (
        <div>
          {data.length === 0 ? (
            <div className="text-center mx-auto pb-20">
              <img
                className="mx-auto py-10"
                alt="tournaments not find "
                src={tournamentsEmptyState}
              />
              <div className="text-2xl mb-5">Nicht gefunden!</div>
              <div>
                Sie sind im Club <b>{myClub?.name}</b> eingeloggt und es gibt
                keine aktiven Turniere für diesen Club.
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Falls Sie denken, dass ein Fehler aufgetreten ist, können Sie
                den
                <Link className="text-[#52A445] font-bold" to="/support">
                  {' '}
                  Support
                </Link>{' '}
                kontaktieren.
              </div>
            </div>
          ) : (
            <Table>
              <TableCaption>Ihre Meldungsübersicht</TableCaption>
              <TableHeader>
                <TableRow>
                  {tableHeader.map((header) => (
                    <TableHead className="whitespace-nowrap" key={header.key}>
                      {header.text}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((cell, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="border-l-2  border-green-500 pl-2">
                        {cell.startDate !== cell.endDate ? (
                          <>📅 Start:</>
                        ) : (
                          <>📅 Start / 🏁 Ende :</>
                        )}{' '}
                        <span className="font-medium">{cell.startDate}</span>
                      </div>
                      {cell.startDate !== cell.endDate ? (
                        <div className="border-l-2  border-red-500 pl-2">
                          🏁 Ende:{' '}
                          <span className="font-medium">{cell.endDate}</span>
                        </div>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium"> {cell.title}</div>
                      <div className="text-xs">
                        Austragungsort: {cell?.venue}
                      </div>
                      {cell.docFile && (
                        <div className="flex text-blue-800 hover:text-blue-900 gap-2 text-center mt-2">
                          <FileText size={20} aria-label="pdf" />
                          <a
                            href={generateDocFileUrl(cell.docFile)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Anhang anzeigen
                          </a>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{cell.registrationEnd}</TableCell>
                    <TableCell onClick={() => resetTorunamentData()}>
                      {(parseGermanDateTime(cell.registrationEnd) ?? Infinity) <= Date.now() ? (
                        <SyncPlayerBtn id={cell.id} name={myClub?.name || ''} />
                      ) : cell.activeRegisterPlayer ? (
                        <Link
                          className="text-[#52A445] hover:underline inline-block w-[120px] font-bold py-0.5"
                          to={`/tournaments/${cell.id}`}
                        >
                          jetzt melden
                        </Link>
                      ) : (
                        '...'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
};

export default TournamentsPage;
